import type * as React from 'react'
import { useEffect, useRef } from 'react'
import type { JSX } from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { Sidebar } from '@buffer-mono/popcorn'

import { useDrawerHeight } from './useDrawerHeight'

import styles from './MobileSidebarDrawer.module.css'

type MobileSidebarDrawerShellProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  footer?: React.ReactNode
  ariaLabel?: string
}

export function MobileSidebarDrawerShell({
  open,
  onOpenChange,
  children,
  footer,
  ariaLabel = 'Sidebar',
}: MobileSidebarDrawerShellProps): JSX.Element {
  const drawerRef = useRef<HTMLElement>(null)

  const fullHeight = useDrawerHeight({
    drawerRef,
    isOpen: open,
  })

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return (): void => {
      document.body.style.overflow = ''
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    const handleEscape = (e: KeyboardEvent): void => {
      // Radix UI components (dropdowns, popovers) handle Escape and call preventDefault()
      // Only close the drawer if the event wasn't already handled
      if (e.key === 'Escape' && !e.defaultPrevented) {
        const trigger = document.querySelector<HTMLButtonElement>(
          '[aria-controls="mobile-navigation-drawer"]',
        )
        onOpenChange(false)
        trigger?.focus()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return (): void => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open, onOpenChange])

  return (
    <AnimatePresence>
      {open && (
        <m.nav
          ref={drawerRef}
          id="mobile-navigation-drawer"
          aria-label={ariaLabel}
          className={styles.navigationDropdown}
          initial={{ height: 0 }}
          animate={{ height: fullHeight }}
          exit={{ height: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          data-sidebar="sidebar"
          data-state="expanded"
        >
          <Sidebar.Provider>
            <div className={styles.navigationContent}>
              <div className={styles.navigationBody}>{children}</div>
              {footer ? (
                <footer className={styles.navigationFooter}>{footer}</footer>
              ) : null}
            </div>
          </Sidebar.Provider>
        </m.nav>
      )}
    </AnimatePresence>
  )
}
